import type { CreateSecretariaBody, SecretariaSummary } from '@obras/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button.js';
import { TextAreaField, TextField } from '../../components/ui/Field.js';
import { FormError } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { api, ApiError } from '../../lib/api.js';

const ICON = { strokeWidth: 1.5 } as const;

const VAZIO = { name: '', acronym: '', description: '' };

/**
 * Cadastro e edição de secretaria demandante.
 *
 * Três campos, e é por isso que ele não repete a estrutura do cadastro de
 * empresa: secretaria não tem CNPJ, contrato conosco nem endereço de cobrança —
 * é uma divisão do próprio município.
 *
 * Mora aqui, e não só na página de secretarias, porque o formulário de obra o
 * abre quando a secretaria demandante ainda não existe: uma obra nova costuma
 * chegar antes do cadastro do órgão que a pediu.
 */
export function SecretariaFormModal({
  open,
  editando,
  onClose,
  onSaved,
}: {
  open: boolean;
  editando?: SecretariaSummary | null;
  onClose: () => void;
  onSaved?: (secretaria: SecretariaSummary) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(VAZIO);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);

  const salvar = useMutation({
    mutationFn: (body: CreateSecretariaBody) =>
      editando
        ? api.patch<SecretariaSummary>(`/secretarias/${editando.id}`, body)
        : api.post<SecretariaSummary>('/secretarias', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['secretarias'] }),
  });

  // Mesmo padrão das demais telas de edição: carrega na primeira renderização.
  if (open && !pronto) {
    setForm(
      editando
        ? {
            name: editando.name,
            acronym: editando.acronym ?? '',
            description: editando.description ?? '',
          }
        : VAZIO,
    );
    setPronto(true);
  }

  const set = (key: keyof typeof VAZIO, value: string): void =>
    setForm((current) => ({ ...current, [key]: value }));

  function fechar(): void {
    setForm(VAZIO);
    setPronto(false);
    setFieldErrors({});
    setFormError(null);
    onClose();
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const orUndefined = (value: string): string | undefined =>
      value.trim() === '' ? undefined : value;

    try {
      const salva = await salvar.mutateAsync({
        name: form.name.trim(),
        acronym: orUndefined(form.acronym),
        description: orUndefined(form.description),
        status: 'ativa',
      });

      onSaved?.(salva);
      fechar();
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);
      } else {
        setFormError('Não foi possível salvar. Tente novamente.');
      }
    }
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title={editando ? 'Editar secretaria' : 'Nova secretaria'}
      description="Órgão interno que demanda obras e responde por elas."
      footer={
        <>
          <Button variant="ghost" onClick={fechar} disabled={salvar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            form="secretaria-form"
            type="submit"
            loading={salvar.isPending}
            disabled={form.name.trim().length < 3}
            icon={<Check {...ICON} size={16} />}
          >
            {editando ? 'Salvar' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      <form
        id="secretaria-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        {formError ? <FormError>{formError}</FormError> : null}

        <TextField
          label="Nome"
          required
          placeholder="Secretaria Municipal de Educação"
          value={form.name}
          onChange={(event) => set('name', event.target.value)}
          {...(fieldErrors['name'] ? { error: fieldErrors['name'] } : {})}
        />

        <TextField
          label="Sigla"
          mono
          maxLength={12}
          placeholder="SEDUC"
          hint="Aparece nas listagens e em colunas estreitas."
          value={form.acronym}
          /* Maiúsculas na digitação: é assim que a sigla é gravada e exibida. */
          onChange={(event) => set('acronym', event.target.value.toUpperCase())}
          {...(fieldErrors['acronym'] ? { error: fieldErrors['acronym'] } : {})}
        />

        <TextAreaField
          label="Descrição"
          rows={3}
          hint="Área de atuação da secretaria, em uma linha."
          value={form.description}
          onChange={(event) => set('description', event.target.value)}
          {...(fieldErrors['description'] ? { error: fieldErrors['description'] } : {})}
        />
      </form>
    </Modal>
  );
}
